/**
 * Hostel Rooms manager — opened from the Hostels admin page.
 *
 * Lets super_admin (or the matching hostel_admin) manage rooms + bed capacity
 * for one hostel. Uses:
 *   GET    /api/admin/hostels/{hostel_id}/rooms
 *   POST   /api/admin/hostels/{hostel_id}/rooms
 *   PATCH  /api/admin/class-rooms/{room_id}
 *   DELETE /api/admin/class-rooms/{room_id}
 *
 * Live occupancy is computed server-side by counting active students whose
 * class_name/section/room_no match. Deletion is blocked when occupancy > 0.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, FloppyDisk, Trash, X, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const OCC_FULL_CLASS = "border-[var(--vs-danger)]/40 text-[var(--vs-danger)] bg-[var(--vs-danger)]/10";
const OCC_HIGH_CLASS = "border-[var(--vs-warning)]/40 text-[var(--vs-warning)] bg-[var(--vs-warning)]/10";
const OCC_FREE_CLASS = "border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10";

function occupancyClass(pct) {
  if (pct >= 100) return OCC_FULL_CLASS;
  if (pct >= 80) return OCC_HIGH_CLASS;
  return OCC_FREE_CLASS;
}

function OccupancyBadge({ occupied, total }) {
  const pct = total > 0 ? Math.round((occupied / total) * 100) : 0;
  return (
    <Badge variant="outline" className={occupancyClass(pct)}>
      {occupied}/{total} ({pct}%)
    </Badge>
  );
}

function RoomEditor({ initial, onSave, onCancel, busy }) {
  const [room_no, setRoomNo] = useState(initial?.room_no || "");
  const [total_seats, setTotalSeats] = useState(initial?.total_seats || 4);
  const [section, setSection] = useState(initial?.section || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ room_no: room_no.trim(), total_seats: Number(total_seats), section: section.trim() || null, notes: notes.trim() || null });
      }}
      className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end p-3 bg-[var(--vs-bg-subtle)] rounded-md mb-3"
    >
      <div>
        <Label className="text-xs">Room #</Label>
        <Input data-testid="hr-room-no" required value={room_no} onChange={(e) => setRoomNo(e.target.value)} placeholder="101A" className="font-mono bg-white" />
      </div>
      <div>
        <Label className="text-xs">Wing / section</Label>
        <Input data-testid="hr-section" value={section} onChange={(e) => setSection(e.target.value)} placeholder="North" className="bg-white" />
      </div>
      <div>
        <Label className="text-xs">Bed capacity</Label>
        <Input data-testid="hr-seats" required type="number" min="1" max="100" value={total_seats} onChange={(e) => setTotalSeats(e.target.value)} className="font-mono bg-white" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs">Notes</Label>
        <Input data-testid="hr-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ground floor, near gate" className="bg-white" />
      </div>
      <div className="md:col-span-5 flex gap-2 justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={busy}><X size={12} className="mr-1" /> Cancel</Button>
        <Button type="submit" size="sm" disabled={busy} className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]"><FloppyDisk size={12} className="mr-1" /> {busy ? "Saving…" : "Save"}</Button>
      </div>
    </form>
  );
}

export default function HostelRoomsDialog({ hostel, open, onOpenChange }) {
  const [rooms, setRooms] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!hostel) return;
    const r = await api.get(`/admin/hostels/${hostel.id}/rooms`);
    setRooms(r.data);
  }, [hostel]);

  useEffect(() => {
    if (open && hostel) refresh();
  }, [open, hostel, refresh]);

  const create = async (payload) => {
    setBusy(true);
    try {
      await api.post(`/admin/hostels/${hostel.id}/rooms`, payload);
      toast.success(`Room ${payload.room_no} created`);
      setAdding(false);
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const update = async (roomId, payload) => {
    setBusy(true);
    try {
      await api.patch(`/admin/class-rooms/${roomId}`, payload);
      toast.success("Room updated");
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (room) => {
    if (!window.confirm(`Delete room ${room.room_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/class-rooms/${room.id}`);
      toast.success(`Room ${room.room_no} deleted`);
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  if (!hostel) return null;

  const totalSeats = rooms.reduce((s, r) => s + (r.total_seats || 0), 0);
  const totalOccupied = rooms.reduce((s, r) => s + (r.occupied || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="hostel-rooms-dialog">
        <DialogHeader>
          <DialogTitle>
            Rooms — {hostel.name}
            <Badge variant="outline" className="ml-2 font-mono text-[10px]">
              {rooms.length} rooms · {totalOccupied}/{totalSeats} beds occupied
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-[var(--vs-text-secondary)]">
            Bed capacity = max students that can live in this room. Live occupancy is computed from active student allocations.
          </p>
          {!adding && (
            <Button size="sm" onClick={() => setAdding(true)} data-testid="hr-add-btn" className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)]">
              <Plus size={12} className="mr-1" /> Add room
            </Button>
          )}
        </div>

        {adding && (
          <RoomEditor initial={null} onSave={create} onCancel={() => setAdding(false)} busy={busy} />
        )}

        <div className="vs-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--vs-bg-subtle)]">
              <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
                <th className="px-3 py-2">Room #</th>
                <th className="px-3 py-2">Section / Wing</th>
                <th className="px-3 py-2">Capacity</th>
                <th className="px-3 py-2">Occupancy</th>
                <th className="px-3 py-2">Notes</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="hr-rows">
              {rooms.map((room) => (
                <React.Fragment key={room.id}>
                  <tr data-testid={`hr-row-${room.id}`} className="border-t border-[var(--vs-border)]">
                    <td className="px-3 py-2 font-mono font-semibold">{room.room_no}</td>
                    <td className="px-3 py-2 text-xs">{room.section || "—"}</td>
                    <td className="px-3 py-2 font-mono">{room.total_seats}</td>
                    <td className="px-3 py-2"><OccupancyBadge occupied={room.occupied} total={room.total_seats} /></td>
                    <td className="px-3 py-2 text-xs text-[var(--vs-text-secondary)] truncate max-w-[200px]">{room.notes || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(room.id)} data-testid={`hr-edit-${room.id}`}><PencilSimple size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(room)} data-testid={`hr-delete-${room.id}`}><Trash size={12} /></Button>
                    </td>
                  </tr>
                  {editing === room.id && (
                    <tr className="border-t border-[var(--vs-border)]">
                      <td colSpan={6} className="px-2 pt-2 pb-3">
                        <RoomEditor
                          initial={room}
                          onSave={(payload) => update(room.id, payload)}
                          onCancel={() => setEditing(null)}
                          busy={busy}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {rooms.length === 0 && !adding && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--vs-text-secondary)]">No rooms defined yet. Click <strong>Add room</strong> to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
